import { DriversLicense, IdReverse, Military, Passport, PassportCard, StepType } from "@cerebruminc/cerebellum";
import { ReactNode } from "react";
import { paths } from "src/const";
import { DocumentTypeValue } from "./views/types";

export const MAX_FAILED_UPLOAD_COUNT = 2;

export const fieldNames = {
	idFront: "idFront",
	idBack: "idBack",
	selfie: "selfie",
};

// All steps are conditional, but will be in this order
export enum VidStepsEnum {
	Selfie = "vid/selfie", // One
	IdUpload = "vid/id-upload", // Two
	Ssn = "vid/ssn", // Three
	SignDisclosures = "vid/sign-disclosures", // Four
	AdditionalInformation = "vid/additional-information", // Five
}

// Default vid steps
export const defaultVidSteps: StepType[] = [
	{
		id: VidStepsEnum.Selfie,
		label: "Liveness Detection",
	},
	{
		id: VidStepsEnum.IdUpload,
		label: "ID Scan and Review",
	},
	{
		id: VidStepsEnum.Ssn,
		label: "SSN",
	},
	{
		id: VidStepsEnum.SignDisclosures,
		label: "Disclosures",
	},
];

// ------ Segmentation State -------
export enum IdCategory {
	INTERNATIONAL = "INTERNATIONAL",
	US = "US",
	NO_VALID_ID = "NO_VALID_ID",
	MANUAL_ENTRY = "MANUAL_ENTRY",
}

export enum UsIdType {
	MILITARY_ID = "MILITARY_ID",
	PASSPORT_BOOKLET = "PASSPORT_BOOKLET",
	PASSPORT_CARD = "PASSPORT_CARD",
	STATE_ID = "STATE_ID",
	TRIBAL_ID = "TRIBAL_ID",
}

export enum InternationalIdType {
	PASSPORT_BOOKLET = "PASSPORT_BOOKLET",
	PASSPORT_CARD = "INTERNATIONAL_ID_CARD",
}

type IdTypeOption = {
	icon?: ReactNode;
	label?: string;
	nextPath: string;
	text?: ReactNode;
	value: IdCategory;
};

const us = {
	icon: <img src="/static/icons/usa.svg" alt="usa-icon" />,
	label: "U.S. ID",
	nextPath: paths.vidUsIdType,
	text: (
		<>
			I have a <b>valid</b> U.S.-issued ID.
		</>
	),
	value: IdCategory.US,
};
const international = {
	icon: <img src="/static/icons/global.svg" alt="global-icon" />,
	label: "International ID",
	nextPath: paths.vidInternationalIdType,
	text: (
		<>
			My <b>valid</b> passport or ID card is from outside the U.S.
		</>
	),
	value: IdCategory.INTERNATIONAL,
};
const invalid = {
	nextPath: paths.vidIdUpload,
	label: "I have a temporary ID.",
	value: IdCategory.NO_VALID_ID,
};
const manualEntryDirect = {
	nextPath: paths.vidManualEntry,
	label: "I'll enter my information manually.",
	value: IdCategory.MANUAL_ENTRY,
};

export type PackageSetMetadata = {
	allowedIdTypes?: {
		[IdCategory.US]: {
			[UsIdType.STATE_ID]: boolean;
			[UsIdType.PASSPORT_BOOKLET]: boolean;
			[UsIdType.PASSPORT_CARD]: boolean;
			[UsIdType.TRIBAL_ID]: boolean;
			[UsIdType.MILITARY_ID]: boolean;
		};
		[IdCategory.INTERNATIONAL]: {
			[InternationalIdType.PASSPORT_BOOKLET]: boolean;
			[InternationalIdType.PASSPORT_CARD]: boolean;
		};
	};
	allowInternationalAddress: boolean;
	allowAliasName?: boolean;
	allowAdditionalAddress?: boolean;
	embedVidWeb?: boolean;
	idScanOptions?: {
		bypassLivenessDetection?: boolean;
		allowDirectManualEntry?: boolean;
	};
};

export const getIdTypeOptions = (metadata: PackageSetMetadata): IdTypeOption[] => {
	const options: IdTypeOption[] = [];

	// If we have US ID options, add at least one of the options is allowed
	if (metadata?.allowedIdTypes?.US && Object.values(metadata.allowedIdTypes.US).some(Boolean)) {
		options.push(us);
	}

	// If we have international ID options, add at least one of the options is allowed
	if (
		metadata?.allowInternationalAddress &&
		metadata?.allowedIdTypes?.INTERNATIONAL &&
		Object.values(metadata.allowedIdTypes.INTERNATIONAL).some(Boolean)
	) {
		options.push(international);
	}

	// If allowDirectManualEntry is set, allow a direct to manual entry option. Temporary IDs are not necessary for an extraction only flow.
	// NOTE: if this is ever removed, we need to conditionally bypass the first step of ID segmentation when there is only one option.
	if (metadata?.idScanOptions?.allowDirectManualEntry) {
		options.push(manualEntryDirect);
	} else {
		options.push(invalid);
	}

	return options;
};

enum BlurCheckType {
	US_STATE_ID = "usStateId",
	US_MILITARY_ID = "usMilitaryId",
	US_TRIBAL_ID = "usTribalId",
	US_PASSPORT_CARD = "usPassportCard",
}

export type ScanIDFormat = {
	altIdFlow: boolean;
	backPath: string;
	blurCheckType?: BlurCheckType;
	detectBarcodeSide?: "front" | "back";
	detectBarcodeType?: "barcode" | "mrz";
	documentType?: DocumentTypeValue;
	icon: ReactNode;
	instructions: string;
	label: string;
	language: "eng" | "eng+osd";
	nextPath: string;
	onlyManual?: boolean;
	scanBack: boolean;
	value: UsIdType | InternationalIdType;
};

export const ScanIdFormats: {
	[K in Exclude<IdCategory, IdCategory.NO_VALID_ID | IdCategory.MANUAL_ENTRY>]: ScanIDFormat[];
} = {
	[IdCategory.US]: [
		{
			altIdFlow: false,
			backPath: paths.vidUsIdType,
			blurCheckType: BlurCheckType.US_STATE_ID,
			detectBarcodeSide: "back",
			detectBarcodeType: "barcode",
			documentType: "Dl",
			icon: <DriversLicense />,
			instructions: "Please use a valid driver's license or state-issued ID.",
			label: "Driver's License or State ID",
			language: "eng",
			nextPath: paths.vidUsIdType,
			scanBack: true,
			value: UsIdType.STATE_ID,
		},
		{
			altIdFlow: true,
			backPath: paths.vidUsIdType,
			// MRZ check suffices for blur test
			detectBarcodeSide: "front",
			detectBarcodeType: "mrz",
			documentType: "Passport",
			icon: <Passport />,
			instructions: "Please use a valid U.S. government-issued passport booklet.",
			label: "Passport (booklet)",
			language: "eng",
			nextPath: paths.vidIdUpload,
			scanBack: false,
			value: UsIdType.PASSPORT_BOOKLET,
		},
		{
			altIdFlow: false,
			backPath: paths.vidUsIdType,
			blurCheckType: BlurCheckType.US_PASSPORT_CARD,
			detectBarcodeSide: "back",
			detectBarcodeType: "mrz",
			documentType: "PassportCard",
			icon: <PassportCard />,
			instructions: "Please use a valid U.S. government-issued passport card.",
			label: "Passport Card",
			language: "eng",
			nextPath: paths.vidIdUpload,
			scanBack: true,
			value: UsIdType.PASSPORT_CARD,
		},
		{
			altIdFlow: false,
			backPath: paths.vidUsIdType,
			blurCheckType: BlurCheckType.US_TRIBAL_ID,
			documentType: "TribalId",
			icon: <IdReverse />,
			instructions: "Please use a valid tribal ID.",
			label: "Tribal ID",
			language: "eng",
			nextPath: paths.vidIdUpload,
			onlyManual: true,
			scanBack: true,
			value: UsIdType.TRIBAL_ID,
		},
		{
			altIdFlow: false,
			backPath: paths.vidUsIdType,
			blurCheckType: BlurCheckType.US_MILITARY_ID,
			detectBarcodeSide: "back",
			detectBarcodeType: "barcode",
			documentType: "MilitaryId",
			icon: <Military />,
			instructions: "Please use a valid U.S. military ID.",
			label: "Military ID",
			language: "eng",
			nextPath: paths.vidIdUpload,
			scanBack: true,
			value: UsIdType.MILITARY_ID,
		},
	],
	[IdCategory.INTERNATIONAL]: [
		{
			altIdFlow: true,
			backPath: paths.vidInternationalIdType,
			// MRZ check suffices for blur test
			detectBarcodeSide: "front",
			detectBarcodeType: "mrz",
			documentType: "Passport",
			icon: <Passport />,
			instructions: "Please use a valid government-issued passport booklet.",
			label: "Passport Booklet",
			language: "eng+osd",
			nextPath: paths.vidIdUpload,
			scanBack: false,
			value: InternationalIdType.PASSPORT_BOOKLET,
		},
		{
			altIdFlow: true,
			backPath: paths.vidInternationalIdType,
			// Passport cards can have an MRZ on either side, and are not guaranteed to have any English text
			// Therefore, we are not running text based legibility checks on these.
			documentType: "PassportCard",
			icon: <PassportCard />,
			instructions: "Please use a valid government-issued ID card.",
			label: "ID Card",
			language: "eng+osd",
			nextPath: paths.vidIdUpload,
			scanBack: true,
			value: InternationalIdType.PASSPORT_CARD,
		},
	],
};
